import { action, observable, override } from 'mobx';

import { IApiClient } from '../data/client';
import { GetModularConfigurationByCodeQuery, GetModularLayoutQuery, GetModularProductFamilyQuery } from '../data/model';
import type {
  ModularConfigurationData,
  ModularLayoutBasicData,
  ModularProductFamilyData,
  ShoppingContext,
  StoreContext,
} from '../data/model';
import { ModularLayoutData } from '../data/model';
import { SiteTranslation } from '../localization/SiteTranslation';
import { BasePageState } from '../shared/BasePageState';
import { IMemento } from '../shared/common';
import Logger from '../shared/Logger';
import { INavigationService } from '../shared/NavigationService';
import { IShareModularConfiguratorUrlProvider } from '../shared/ShareModularConfiguratorUrlProvider';
import { StoreState } from '../StoreState';
import ModularConfiguratorCoordinatorState from './components/ModularConfiguratorCoordinator/ModularConfiguratorCoordinatorState';
import ModularConfiguratorPageQuery from './ModularConfiguratorPageQuery';
import ModularConfiguratorPageQueryParser from './ModularConfiguratorPageQueryParser';

interface IModularConfiguratorPageMemento extends IMemento {
  configurationData: ModularConfigurationData;
  productFamilyData: ModularProductFamilyData;
  shoppingContext: ShoppingContext;
  storeContext: StoreContext;
}

class ModularConfiguratorPageState extends BasePageState<IModularConfiguratorPageMemento> {
  public configuratorCoordinator: ModularConfiguratorCoordinatorState;

  @observable
  private configurationData: ModularConfigurationData;
  private productFamilyData: ModularProductFamilyData;
  public readonly query: ModularConfiguratorPageQuery;
  private shoppingContext: ShoppingContext;
  private storeContext: StoreContext;

  @override
  public get metadata() {
    // TODO: Get from translations
    return { title: `${this.productFamilyData.name} - Configurator` };
  }

  public constructor(
    private readonly client: IApiClient,
    private readonly configurationUrlProvider: IShareModularConfiguratorUrlProvider,
    private readonly navigation: INavigationService,
    // TODO: Replace with ModularConfiguratorPageTranslation
    private readonly translation: SiteTranslation,
    private readonly productFamilyId: string,
  ) {
    super();

    this.query = ModularConfiguratorPageQueryParser.toModel(this.navigation.currentUrl.query);
  }

  public getMemento(): IModularConfiguratorPageMemento {
    return {
      configurationData: this.configurationData,
      productFamilyData: this.productFamilyData,
      shoppingContext: this.shoppingContext,
      storeContext: this.storeContext,
    };
  }

  public restoreMemento(memento: IModularConfiguratorPageMemento) {
    this.initialize(
      memento.productFamilyData,
      memento.configurationData,
      memento.shoppingContext,
      memento.storeContext,
    );
  }

  public async onLoad(store: StoreState) {
    const { code, layoutCode } = this.query;
    const productFamilyData = await this.loadProductFamilyData();

    let configurationData: ModularConfigurationData;

    if (code) {
      try {
        const response = await this.client.send(new GetModularConfigurationByCodeQuery({ modularCode: code }));
        configurationData = response.modularConfigurationData;
      } catch (error) {
        Logger.warn(`Modular configuration with code '${code}' could not be restored. Error: ${error}`);
      }
    }

    if (!configurationData) {
      const initialLayoutId = this.resolveInitialLayoutId(layoutCode, productFamilyData);
      const layoutData = await this.loadLayoutData(initialLayoutId);
      configurationData = this.buildConfigurationForLayout(layoutData);
    }

    this.initialize(productFamilyData, configurationData, store.shoppingContext, store.storeResponse.storeContext);
  }

  public async onLoadAdditionalData() {
    await this.loadConfigurationIntoConfigurator(this.configurationData);
  }

  private initialize(
    productFamilyData: ModularProductFamilyData,
    configurationData: ModularConfigurationData,
    shoppingContext: ShoppingContext,
    storeContext: StoreContext,
  ) {
    this.configuratorCoordinator = new ModularConfiguratorCoordinatorState(
      this.client,
      this.configurationUrlProvider,
      productFamilyData,
      shoppingContext,
      this.translation,
      configurationData.layoutId,
      this.navigation,
      this.query,
    );
    this.configurationData = configurationData;
    this.productFamilyData = productFamilyData;
    this.shoppingContext = shoppingContext;
    this.storeContext = storeContext;

    this.configuratorCoordinator.layoutPicker.changed.subscribe(this.handleLayoutChangeEvent);

    this.configurationUrlProvider.initialize(productFamilyData.id);
  }

  @action.bound
  private async handleLayoutChangeEvent({ code, id }: ModularLayoutBasicData) {
    this.updateQueryString(new ModularConfiguratorPageQuery(code, ''));
    await this.reloadLayout(id);
  }

  @action
  private async reloadLayout(layoutId: string) {
    const layoutData = await this.loadLayoutData(layoutId);
    this.configurationData = this.buildConfigurationForLayout(layoutData);
    await this.loadConfigurationIntoConfigurator(this.configurationData);
  }

  private async loadLayoutData(layoutId: string) {
    const query = new GetModularLayoutQuery({ layoutId, shoppingContext: this.shoppingContext });
    const response = await this.client.send(query);

    return response.layout;
  }

  private async loadConfigurationIntoConfigurator(configurationData: ModularConfigurationData) {
    return this.configuratorCoordinator.loadConfigurationIntoConfigurator(configurationData);
  }

  private async loadProductFamilyData() {
    const query = new GetModularProductFamilyQuery({
      productFamilyId: this.productFamilyId,
      shoppingContext: this.shoppingContext,
    });
    const response = await this.client.send(query);

    return response.productFamily;
  }

  private buildConfigurationForLayout(layoutData: ModularLayoutData): ModularConfigurationData {
    return {
      layoutId: layoutData.code,
      productFamilyId: this.productFamilyId,
      components: layoutData.components.map((x) => ({ productConfigurationCode: '', ...x })),
    };
  }

  private resolveInitialLayoutId(layoutCode: string, productFamilyData: ModularProductFamilyData) {
    const layout = productFamilyData.layouts.find((x) => x.code === layoutCode);

    if (layout?.id) {
      return layout.id;
    }

    return productFamilyData.layouts.first()?.id ?? '';
  }

  private updateQueryString(queryModel: ModularConfiguratorPageQuery) {
    this.navigation.setQuery(ModularConfiguratorPageQueryParser.toQuery(queryModel));
  }
}

export default ModularConfiguratorPageState;
